/**
 * studio.js — Faber Maison Shopify Theme
 *
 * Studio state machine (vanilla JS) matching Next.js UI:
 * Features:
 *  - Color picker → swaps main image + peek image + label image
 *  - Size picker
 *  - Make It Personal (Upsell) → reveals Note Composer
 *  - Note Composer (To / Note / From)
 *  - Live CSS text overlay on label image
 *  - Borrow a line (quotes)
 *  - Character limits
 *  - AJAX add to cart → opens cart drawer
 */

(function () {
  'use strict';

  // ── Constants ──────────────────────────────────────────────
  const LIMITS = {
    to: 24,
    from: 26,
    note: 80,
    noteRows: 2
  };

  // ── State ──────────────────────────────────────────────────
  const state = {
    tier: 'base',          // 'base' | 'personalized'
    selectedColor: 'cloud',
    selectedColorLabel: '',
    selectedColorShopify: '',
    selectedOptions: {},
    selectedFont: 'mono',
    to: '',
    note: '',
    from: '',
    isFocused: false,
    isAdding: false,
    ghostIndex: 0,
    ghostTimer: null,
  };

  // ── DOM refs ────────────────────────────────────────────────
  const $ = (id) => document.getElementById(id);
  const $$ = (sel, ctx = document) => ctx.querySelectorAll(sel);

  const app = $('StudioApp');
  if (!app) return; // not on a page with studio

  // Images
  const mainImg = $('StudioMainImg');
  const peekImg = $('StudioPeekImg');
  const labelImg = $('StudioLabelImg');
  const basePreviewLayer = $('StudioBasePreview');
  const persPreviewLayer = $('StudioPersonalizedPreview');
  
  // Text Overlay
  const labelText = $('StudioLabelText');

  // Controls
  const colorSwatches = $$('.color-swatch', app);
  const colorNameEl = $('StudioColorName');
  const optionButtons = $$('.size-btn', app);
  const priceMain = $('StudioPriceMain');
  const addBtn = $('StudioAddBtn');
  const errorMsg = $('StudioErrorMsg');

  // Personalization
  const baseUpsell = $('StudioBaseUpsell');
  const upgradeBtn = $('StudioUpgradeBtn');
  const noteComposer = $('StudioNoteComposer');
  const removeNoteBtn = $('StudioRemoveNoteBtn');
  const paperSurface = $('StudioPaperSurface');

  // Inputs
  const toInput = $('StudioToInput');
  const toComma = $('StudioToComma');
  const msgInput = $('StudioMessageInput');
  const fromInput = $('StudioFromInput');
  const charCount = $('StudioCharCount');

  // Quotes
  const quotesToggle = $('StudioQuotesToggle');
  const quotesList = $('StudioQuotesList');
  const quoteBtns = $$('.studio-quote-btn', quotesList);

  // ── Config from Liquid data attrs ─────────────────────────
  const studioConfig = window.FaberStudio || {};
  const images = studioConfig.images || {};
  const ghostMessages = studioConfig.ghostMessages || ['Your words, hidden close.'];
  const basePriceMoney = app.dataset.basePriceMoney || '$79';
  const personalizedPriceMoney = app.dataset.personalizedPriceMoney || '$90';
  const baseBtnMoney = app.dataset.baseBtnMoney || '$79';
  const personalizedBtnMoney = app.dataset.personalizedBtnMoney || '$90';
  const upsellVariantId = app.dataset.upsellVariantId || '42975319195746';

  // ── Initialization ─────────────────────────────────────────
  function init() {
    // Read initial selected color from the DOM
    const activeColor = Array.from(colorSwatches).find(s => s.classList.contains('is-active'));
    if (activeColor) {
      state.selectedColor = activeColor.dataset.colorId;
      state.selectedColorLabel = activeColor.dataset.colorLabel || activeColor.dataset.colorId;
      state.selectedColorShopify = activeColor.dataset.shopifyColor || activeColor.dataset.colorLabel;
    }

    // Read initial selected values for ALL non-color options
    optionButtons.forEach(btn => {
      const optName = btn.dataset.optionName;
      if (optName && btn.classList.contains('is-active')) {
        state.selectedOptions[optName] = btn.dataset.size;
      }
    });

    setTier('base');
    updateImages();
    startGhostRotation();
    attachEvents();
  }

  // ── Events ─────────────────────────────────────────────────
  function attachEvents() {
    // Tier changes
    if (upgradeBtn) upgradeBtn.addEventListener('click', () => {
      setTier('personalized');
      setTimeout(() => toInput && toInput.focus(), 100);
    });

    // Mobile upgrade button → open modal
    const upgradeBtnMobile = $('StudioUpgradeBtnMobile');
    if (upgradeBtnMobile) upgradeBtnMobile.addEventListener('click', () => {
      openMobileModal();
    });
    
    if (removeNoteBtn) removeNoteBtn.addEventListener('click', () => {
      setTier('base');
      clearNote();
    });

    // Color swatches
    colorSwatches.forEach(swatch => {
      swatch.addEventListener('click', () => {
        state.selectedColor = swatch.dataset.colorId;
        state.selectedColorLabel = swatch.dataset.colorLabel || swatch.dataset.colorId;
        state.selectedColorShopify = swatch.dataset.shopifyColor || swatch.dataset.colorLabel;

        colorSwatches.forEach(s => s.classList.remove('is-active'));
        swatch.classList.add('is-active');

        if (colorNameEl) colorNameEl.textContent = state.selectedColorLabel;

        updateImages();
      });
    });

    // Option buttons (Style, Size, etc.)
    optionButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-sold-out')) return;
        const optName = btn.dataset.optionName;
        state.selectedOptions[optName] = btn.dataset.size;

        // Deactivate siblings in the same option group
        const row = btn.closest('.size-row');
        if (row) row.querySelectorAll('.size-btn').forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
      });
    });

    // Inputs
    [toInput, msgInput, fromInput].forEach(el => {
      if (!el) return;
      el.addEventListener('focus', () => {
        state.isFocused = true;
        paperSurface && paperSurface.classList.add('is-focused');
        stopGhostRotation();
        updateLabelText();
      });
      el.addEventListener('blur', () => {
        state.isFocused = false;
        paperSurface && paperSurface.classList.remove('is-focused');
        if (!hasContent()) {
          startGhostRotation();
        }
        updateLabelText();
      });
      el.addEventListener('input', handleInput);
    });

    // Quotes
    if (quotesToggle) {
      quotesToggle.addEventListener('click', () => {
        const isOpen = quotesToggle.classList.toggle('is-open');
        if (isOpen) {
          quotesList.style.display = 'flex';
          setTimeout(() => {
            quotesList.style.opacity = '1';
            quotesList.style.maxHeight = '420px';
          }, 10);
        } else {
          quotesList.style.opacity = '0';
          quotesList.style.maxHeight = '0';
          setTimeout(() => { quotesList.style.display = 'none'; }, 300);
        }
      });
    }

    quoteBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        if (msgInput) {
          msgInput.value = btn.dataset.quote || btn.textContent.trim();
          handleInput();
          quotesToggle.click(); // Close
        }
      });
    });

    // Add to cart
    if (addBtn) addBtn.addEventListener('click', addToCart);
  }

  // ── Core logic ─────────────────────────────────────────────
  
  function setTier(tier) {
    state.tier = tier;

    if (tier === 'base') {
      if (baseUpsell) baseUpsell.style.display = 'block';
      if (noteComposer) noteComposer.style.display = 'none';
      if (basePreviewLayer) {
        basePreviewLayer.style.opacity = '1';
        basePreviewLayer.style.pointerEvents = 'auto';
      }
      if (persPreviewLayer) {
        persPreviewLayer.style.opacity = '0';
        persPreviewLayer.style.pointerEvents = 'none';
      }
      startGhostRotation();
    } else {
      if (baseUpsell) baseUpsell.style.display = 'none';
      if (noteComposer) noteComposer.style.display = 'block';
      if (basePreviewLayer) {
        basePreviewLayer.style.opacity = '0';
        basePreviewLayer.style.pointerEvents = 'none';
      }
      if (persPreviewLayer) {
        persPreviewLayer.style.opacity = '1';
        persPreviewLayer.style.pointerEvents = 'auto';
      }
      stopGhostRotation();
    }

    updatePrice();
    updateLabelText();
  }

  function updateImages() {
    const colorKey = state.selectedColor;
    
    // Main Product
    if (mainImg && images.main && images.main[colorKey]) {
      mainImg.src = images.main[colorKey];
    }
    // Circle Peek
    if (peekImg && images.basePeek && images.basePeek[colorKey]) {
      peekImg.src = images.basePeek[colorKey];
    }
    // Label Preview
    if (labelImg && images.personalized && images.personalized[colorKey]) {
      labelImg.src = images.personalized[colorKey];
    }
  }

  function updatePrice() {
    const isPers = state.tier === 'personalized';
    if (priceMain) priceMain.textContent = isPers ? personalizedPriceMoney : basePriceMoney;
    const btnPrice = $('StudioBtnPrice');
    if (btnPrice) btnPrice.textContent = isPers ? personalizedBtnMoney : baseBtnMoney;
  }

  function handleInput() {
    if (toInput) state.to = toInput.value.substring(0, LIMITS.to);
    if (fromInput) state.from = fromInput.value.substring(0, LIMITS.from);
    
    if (msgInput) {
      let val = msgInput.value;
      if (val.length > LIMITS.note) {
        val = val.substring(0, LIMITS.note);
        msgInput.value = val;
      }
      state.note = val;
      if (charCount) {
        charCount.textContent = `${val.length}/${LIMITS.note}`;
        charCount.style.color = val.length >= LIMITS.note ? 'var(--brand-taupe)' : 'rgba(41,37,36,0.45)';
      }
    }

    if (toComma) {
      toComma.style.display = state.to.trim() ? 'inline' : 'none';
    }

    updateLabelText();
  }

  function clearNote() {
    state.to = '';
    state.note = '';
    state.from = '';
    if (toInput) toInput.value = '';
    if (msgInput) msgInput.value = '';
    if (fromInput) fromInput.value = '';
    handleInput();
  }

  function hasContent() {
    return state.to.trim() || state.note.trim() || state.from.trim();
  }

  // ── Ghost text rotation ────────────────────────────────────
  function startGhostRotation() {
    if (state.ghostTimer) return;
    state.ghostTimer = setInterval(() => {
      if (state.tier === 'personalized' && hasContent()) return;
      state.ghostIndex = (state.ghostIndex + 1) % ghostMessages.length;
      showGhostText(ghostMessages[state.ghostIndex]);
    }, 4000);
  }

  function stopGhostRotation() {
    clearInterval(state.ghostTimer);
    state.ghostTimer = null;
  }

  function showGhostText(text) {
    if (!labelText) return;
    labelText.classList.remove('label-ghost-fade');
    void labelText.offsetHeight; // Force reflow
    labelText.classList.add('ghost-text', 'label-ghost-fade');
    labelText.innerHTML = `<span style="display:block; text-align:center;">${text}</span>`;
  }

  function updateLabelText() {
    if (!labelText) return;

    if (state.tier !== 'personalized' || (!hasContent() && !state.isFocused)) {
      labelText.classList.add('ghost-text');
      if (!state.isFocused) {
        // Will be updated by ghost timer
      } else {
        labelText.innerHTML = `<span style="display:block; text-align:center;">${ghostMessages[state.ghostIndex]}</span>`;
      }
      return;
    }

    labelText.classList.remove('ghost-text', 'label-ghost-fade');
    
    // Build HTML for woven label
    let html = '';
    if (state.to.trim()) {
      html += `<span style="display:block; text-align:left;">${state.to},</span>`;
    }
    if (state.note.trim()) {
      html += `<span style="display:block; text-align:left; white-space:pre-wrap;">${state.note}</span>`;
    }
    if (state.from.trim()) {
      html += `<span style="display:block; text-align:right;">${state.from}</span>`;
    }
    
    labelText.innerHTML = html || ' ';
  }

  // ── Shopify AJAX add to cart ───────────────────────────────
  async function findVariantId() {
    const productHandle = studioConfig.productHandle || app.dataset.productHandle;
    if (!productHandle) return null;

    try {
      const res = await fetch(`/products/${productHandle}.js`);
      const product = await res.json();

      // Build a target map: { optionIndex: expectedValue }
      const targetMap = {};
      product.options.forEach((opt, idx) => {
        const name = opt?.name || opt; // product.options can be strings or objects
        const nameLower = (typeof name === 'string' ? name : '').toLowerCase();

        if (nameLower === 'color') {
          targetMap[idx] = state.selectedColorShopify.toLowerCase().trim();
        } else {
          // Match from selectedOptions by option name
          const selected = state.selectedOptions[name];
          if (selected) {
            targetMap[idx] = selected.toLowerCase().trim();
          }
        }
      });

      const variant = product.variants.find((v) => {
        const opts = v.options || [];
        return Object.entries(targetMap).every(([idx, target]) => {
          return opts[idx]?.toLowerCase().trim() === target;
        }) && v.available;
      });
      return variant ? variant.id : null;
    } catch {
      return null;
    }
  }

  async function addToCart() {
    if (state.isAdding) return;
    state.isAdding = true;
    if (errorMsg) errorMsg.style.display = 'none';

    if (addBtn) {
      addBtn.disabled = true;
      const lblEl = addBtn.querySelector('.studio-add-btn-label');
      if (lblEl) lblEl.textContent = 'Adding...';
      else addBtn.textContent = 'Adding...';
    }

    try {
      const properties = {
        _Tier: state.tier === 'personalized' ? 'Personalized' : 'Classic',
        _Color: state.selectedColorLabel,
      };
      // Add all selected options as line item properties
      Object.entries(state.selectedOptions).forEach(([key, val]) => {
        properties['_' + key] = val;
      });

      if (state.tier === 'personalized' && hasContent()) {
        const composed = [
          state.to.trim() ? `${state.to},` : '',
          state.note.trim(),
          state.from.trim()
        ].filter(Boolean).join('\n');
        
        properties['_Hidden Message'] = composed;
        properties['_Label Font'] = 'Typewriter';
      }

      let variantId = await findVariantId();

      // Fallback for development if product not found
      if (!variantId && window.location.hostname === '127.0.0.1') {
        console.warn('Development mode: mock variant ID used');
        variantId = 123456789;
      } else if (!variantId) {
        throw new Error('Please configure the studio product in the theme settings.');
      }

      const items = [{ id: variantId, quantity: 1, properties }];

      if (state.tier === 'personalized' && upsellVariantId && upsellVariantId !== '123456') {
        items.push({
          id: parseInt(upsellVariantId, 10),
          quantity: 1,
          properties: { _Hidden_Message_For: variantId.toString() },
        });
      }

      const res = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.description || 'Could not add to cart.');

      // Small delay to let Shopify process the cart update
      await new Promise(r => setTimeout(r, 300));
      await updateCartCount();
      openCartDrawer();

    } catch (err) {
      if (errorMsg) {
        errorMsg.textContent = err.message || 'Something went wrong. Please try again.';
        errorMsg.style.display = 'block';
      }
    } finally {
      state.isAdding = false;
      if (addBtn) {
        addBtn.disabled = false;
        const lblEl = addBtn.querySelector('.studio-add-btn-label');
        if (lblEl) lblEl.textContent = 'Add to Bag';
        else addBtn.textContent = 'Add to Bag';
      }
    }
  }

  async function updateCartCount() {
    try {
      const res = await fetch(`/cart.js?_=${Date.now()}`);
      const cart = await res.json();
      const badge = document.getElementById('CartCountBadge');
      if (badge) {
        badge.textContent = cart.item_count;
        badge.dataset.count = cart.item_count;
        badge.style.display = cart.item_count > 0 ? '' : 'none';
        badge.style.transform = 'scale(1.4)';
        setTimeout(() => { badge.style.transform = ''; }, 300);
      }
    } catch {}
  }

  function openCartDrawer() {
    if (window.FaberCartDrawer && window.FaberCartDrawer.open) {
      window.FaberCartDrawer.open();
    } else {
      // Fallback: manually open + fetch
      const drawer = document.getElementById('CartDrawer');
      if (drawer) {
        drawer.classList.add('is-open');
        drawer.removeAttribute('hidden');
        document.body.style.overflow = 'hidden';
      }
    }
  }

  // ── Mobile Personalization Modal ────────────────────────────
  const mobileModal = $('StudioMobileModal');
  const modalToInput = $('StudioModalToInput');
  const modalNoteInput = $('StudioModalNoteInput');
  const modalFromInput = $('StudioModalFromInput');
  const modalLabelImg = $('StudioModalLabelImg');
  const modalLabelText = $('StudioModalLabelText');
  const modalUseQuote = $('StudioModalUseQuote');
  const modalNextQuote = $('StudioModalNextQuote');
  const modalBrandOnly = $('StudioModalBrandOnly');
  const modalAddBtn = $('StudioModalAddBtn');
  const modalRewrite = $('StudioModalRewrite');
  let modalQuoteIndex = 0;

  // Preset demo messages for typewriter
  const demoMessages = [
    { to: 'My love,', note: 'Every day with you still feels like the very first morning.', from: 'Emma' },
    { to: 'Dear Dad,', note: 'Thank you for everything. I carry your words with me.', from: 'Your kid' },
    { to: 'Hey you,', note: 'Some things you keep forever. You\'re one of them.', from: 'Always' },
    { to: 'To myself,', note: 'Be brave. Be kind. Be you.', from: '' },
  ];
  let twTimer = null;
  let twDemoIdx = 0;
  let twRunning = false;
  let twCancelled = false;

  function openMobileModal() {
    if (!mobileModal) return;
    // Sync color → label image
    const colorKey = state.selectedColor;
    if (modalLabelImg && images.personalized && images.personalized[colorKey]) {
      modalLabelImg.src = images.personalized[colorKey];
    }
    // Pre-fill from main state
    if (modalToInput) modalToInput.value = state.to;
    if (modalNoteInput) modalNoteInput.value = state.note;
    if (modalFromInput) modalFromInput.value = state.from;

    mobileModal.removeAttribute('hidden');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        mobileModal.classList.add('is-open');
      });
    });
    document.body.style.overflow = 'hidden';

    // Start typewriter if fields are empty
    const hasUserContent = (modalToInput && modalToInput.value.trim()) ||
                           (modalNoteInput && modalNoteInput.value.trim()) ||
                           (modalFromInput && modalFromInput.value.trim());
    if (!hasUserContent) {
      startTypewriter();
    } else {
      updateModalLabelText();
    }
  }

  function closeMobileModal() {
    if (!mobileModal) return;
    stopTypewriter();
    mobileModal.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(() => {
      if (!mobileModal.classList.contains('is-open')) {
        mobileModal.setAttribute('hidden', '');
      }
    }, 400);
  }

  // ── Typewriter engine ──────────────────────────
  function startTypewriter() {
    stopTypewriter();
    twCancelled = false;
    twRunning = true;
    typeNextDemo();
  }

  function stopTypewriter() {
    twCancelled = true;
    twRunning = false;
    if (twTimer) { clearTimeout(twTimer); twTimer = null; }
  }

  function typeNextDemo() {
    if (twCancelled) return;
    const demo = demoMessages[twDemoIdx % demoMessages.length];
    // Clear label
    if (modalLabelText) {
      modalLabelText.classList.remove('ghost-text', 'label-ghost-fade');
      modalLabelText.innerHTML = '';
    }

    // Type sequence: to → note → from
    const parts = [];
    if (demo.to) parts.push({ field: 'to', text: demo.to, align: 'left', suffix: '' });
    if (demo.note) parts.push({ field: 'note', text: demo.note, align: 'left', wrap: true });
    if (demo.from) parts.push({ field: 'from', text: demo.from, align: 'right' });

    typePartSequence(parts, 0, () => {
      if (twCancelled) return;
      // Hold for 3s, then fade and type next
      twTimer = setTimeout(() => {
        if (twCancelled) return;
        // Fade out label
        if (modalLabelText) {
          modalLabelText.style.transition = 'opacity 0.4s ease';
          modalLabelText.style.opacity = '0';
        }
        twTimer = setTimeout(() => {
          if (twCancelled) return;
          if (modalLabelText) {
            modalLabelText.style.opacity = '1';
            modalLabelText.innerHTML = '';
          }
          twDemoIdx++;
          typeNextDemo();
        }, 500);
      }, 3000);
    });
  }

  function typePartSequence(parts, idx, onDone) {
    if (twCancelled || idx >= parts.length) { onDone(); return; }
    const part = parts[idx];
    const span = document.createElement('span');
    span.style.display = 'block';
    span.style.textAlign = part.align || 'left';
    if (part.wrap) span.style.whiteSpace = 'pre-wrap';
    if (modalLabelText) modalLabelText.appendChild(span);

    // Also update the NOTE placeholder with current quote
    if (part.field === 'note' && modalNoteInput) {
      modalNoteInput.placeholder = `"${part.text}"`;
    }

    typeChars(span, part.text, 0, () => {
      if (twCancelled) return;
      twTimer = setTimeout(() => {
        typePartSequence(parts, idx + 1, onDone);
      }, 200);
    });
  }

  function typeChars(el, text, charIdx, onDone) {
    if (twCancelled || charIdx >= text.length) { onDone(); return; }
    el.textContent = text.substring(0, charIdx + 1);
    const delay = 40 + Math.random() * 30; // 40-70ms per char
    twTimer = setTimeout(() => typeChars(el, text, charIdx + 1, onDone), delay);
  }

  function updateModalLabelText() {
    if (!modalLabelText) return;
    const to = modalToInput ? modalToInput.value : '';
    const note = modalNoteInput ? modalNoteInput.value : '';
    const from = modalFromInput ? modalFromInput.value : '';

    if (!to.trim() && !note.trim() && !from.trim()) {
      modalLabelText.classList.add('ghost-text');
      modalLabelText.innerHTML = `<span style="display:block; text-align:center;">${ghostMessages[0]}</span>`;
      return;
    }

    modalLabelText.classList.remove('ghost-text', 'label-ghost-fade');
    modalLabelText.style.opacity = '1';
    let html = '';
    if (to.trim()) html += `<span style="display:block; text-align:left;">${to},</span>`;
    if (note.trim()) html += `<span style="display:block; text-align:left; white-space:pre-wrap;">${note}</span>`;
    if (from.trim()) html += `<span style="display:block; text-align:right;">${from}</span>`;
    modalLabelText.innerHTML = html || ' ';
  }

  function syncModalToState() {
    state.to = modalToInput ? modalToInput.value.substring(0, LIMITS.to) : '';
    state.note = modalNoteInput ? modalNoteInput.value.substring(0, LIMITS.note) : '';
    state.from = modalFromInput ? modalFromInput.value.substring(0, LIMITS.from) : '';
    // Sync back to main form
    if (toInput) toInput.value = state.to;
    if (msgInput) msgInput.value = state.note;
    if (fromInput) fromInput.value = state.from;
    handleInput();
  }

  // Stop typewriter on any input focus
  [modalToInput, modalNoteInput, modalFromInput].forEach(el => {
    if (!el) return;
    el.addEventListener('focus', () => {
      if (twRunning) {
        stopTypewriter();
        // Clear the typed demo from label, show ghost
        if (modalLabelText) {
          modalLabelText.classList.add('ghost-text');
          modalLabelText.innerHTML = `<span style="display:block; text-align:center;">${ghostMessages[0]}</span>`;
          modalLabelText.style.opacity = '1';
        }
      }
    });
    el.addEventListener('input', () => {
      stopTypewriter();
      updateModalLabelText();
    });
  });

  // Quote buttons
  if (modalNextQuote) {
    modalNextQuote.addEventListener('click', () => {
      stopTypewriter();
      modalQuoteIndex = (modalQuoteIndex + 1) % ghostMessages.length;
      if (modalNoteInput) {
        modalNoteInput.value = '';
        modalNoteInput.placeholder = `"${ghostMessages[modalQuoteIndex]}"`;
      }
      // Show quote on label
      if (modalLabelText) {
        modalLabelText.classList.remove('ghost-text', 'label-ghost-fade');
        modalLabelText.style.opacity = '1';
        const to = modalToInput ? modalToInput.value : '';
        const from = modalFromInput ? modalFromInput.value : '';
        let html = '';
        if (to.trim()) html += `<span style="display:block; text-align:left;">${to},</span>`;
        html += `<span style="display:block; text-align:left; white-space:pre-wrap; font-style:italic;">${ghostMessages[modalQuoteIndex]}</span>`;
        if (from.trim()) html += `<span style="display:block; text-align:right;">${from}</span>`;
        modalLabelText.innerHTML = html;
      }
    });
  }

  if (modalUseQuote) {
    modalUseQuote.addEventListener('click', () => {
      stopTypewriter();
      if (modalNoteInput) {
        modalNoteInput.value = ghostMessages[modalQuoteIndex];
        modalNoteInput.placeholder = 'Say the thing — it wraps to fit.';
      }
      updateModalLabelText();
    });
  }

  // Rewrite button → clear inputs, restart typewriter
  if (modalRewrite) {
    modalRewrite.addEventListener('click', () => {
      if (modalToInput) modalToInput.value = '';
      if (modalNoteInput) { modalNoteInput.value = ''; modalNoteInput.placeholder = 'Say the thing — it wraps to fit.'; }
      if (modalFromInput) modalFromInput.value = '';
      twDemoIdx = 0;
      startTypewriter();
    });
  }

  // Brand Tag Only → close modal, stay base
  if (modalBrandOnly) {
    modalBrandOnly.addEventListener('click', () => {
      closeMobileModal();
    });
  }

  // Modal Add to Bag → set personalized, sync, add to cart, close
  if (modalAddBtn) {
    modalAddBtn.addEventListener('click', async () => {
      syncModalToState();
      setTier('personalized');
      closeMobileModal();
      await addToCart();
    });
  }

  // Start
  init();

})();
